/* GRIPHAUS — shared behaviour for Dawn. Safe to load more than once. */
(function () {
  if (window.__griphausLoaded) return;
  window.__griphausLoaded = true;

  /* Before / after comparison slider */
  if (!customElements.get('gh-before-after')) {
    customElements.define('gh-before-after', class extends HTMLElement {
      connectedCallback() {
        const range = this.querySelector('input[type="range"]');
        if (!range) return;
        const update = () => this.style.setProperty('--gh-ba-pos', range.value + '%');
        range.addEventListener('input', update);
        update();
      }
    });
  }

  /* Open / close <dialog> elements: any element with data-gh-open="dialogId" */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-gh-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('data-gh-open'));
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        dialog.showModal();
        dialog.dataset.ghReturnFocus = '1';
        dialog._ghOpener = opener;
      }
      return;
    }
    const closer = event.target.closest('[data-gh-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) dialog.close();
      return;
    }
    /* Click on the backdrop closes the dialog */
    if (event.target.tagName === 'DIALOG' && event.target.classList.contains('gh-dialog')) {
      const rect = event.target.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) event.target.close();
    }
  });
  document.addEventListener('close', (event) => {
    const dialog = event.target;
    if (dialog && dialog._ghOpener) {
      dialog._ghOpener.focus();
      dialog._ghOpener = null;
    }
  }, true);

  /* UGC videos: play / pause on button press */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-gh-play]');
    if (!btn) return;
    const item = btn.closest('.gh-ugc__item');
    const video = item && item.querySelector('video');
    if (!video) return;
    if (video.paused) {
      document.querySelectorAll('.gh-ugc__item video').forEach((v) => { if (v !== video) v.pause(); });
      video.play();
      btn.setAttribute('aria-label', btn.dataset.labelPause || 'Pause video');
    } else {
      video.pause();
      btn.setAttribute('aria-label', btn.dataset.labelPlay || 'Play video');
    }
  });

  /* Free-shipping progress bar: <gh-freeship data-threshold="4900"> (cents) */
  if (!customElements.get('gh-freeship')) {
    customElements.define('gh-freeship', class extends HTMLElement {
      connectedCallback() {
        this.threshold = parseInt(this.dataset.threshold, 10) || 4900;
        this.refresh = this.refresh.bind(this);
        document.addEventListener('gh:cart-changed', this.refresh);
        /* Dawn re-renders cart markup after every change; watch for it */
        const watch = document.querySelector('cart-items, cart-drawer-items, #main-cart-items');
        if (watch && 'MutationObserver' in window) {
          this.observer = new MutationObserver(() => {
            clearTimeout(this._t);
            this._t = setTimeout(this.refresh, 250);
          });
          this.observer.observe(watch, { childList: true, subtree: true });
        }
      }
      disconnectedCallback() {
        document.removeEventListener('gh:cart-changed', this.refresh);
        if (this.observer) this.observer.disconnect();
      }
      format(cents) {
        const tpl = this.dataset.moneyFormat || '${{amount}}';
        const amount = (cents / 100).toFixed(2);
        return tpl.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
      }
      refresh() {
        fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => this.render(cart.total_price))
          .catch(() => {});
      }
      render(total) {
        const remaining = Math.max(0, this.threshold - total);
        const pct = Math.min(100, Math.round((total / this.threshold) * 100));
        const msg = this.querySelector('[data-gh-freeship-msg]');
        const bar = this.querySelector('[data-gh-freeship-bar]');
        if (bar) bar.style.width = pct + '%';
        if (msg) {
          msg.textContent = remaining > 0
            ? (this.dataset.textRemaining || "You're [amount] away from free US shipping").replace('[amount]', this.format(remaining))
            : (this.dataset.textDone || "You've unlocked free US shipping");
        }
        this.setAttribute('aria-valuenow', String(pct));
      }
    });
  }

  /* Sticky add-to-cart bar on product pages */
  if (!customElements.get('gh-sticky-atc')) {
    customElements.define('gh-sticky-atc', class extends HTMLElement {
      connectedCallback() {
        const target = document.querySelector('product-form, .product-form, form[action*="/cart/add"]');
        if (!target || !('IntersectionObserver' in window)) return;
        this.io = new IntersectionObserver(([entry]) => {
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', passed);
          this.setAttribute('aria-hidden', passed ? 'false' : 'true');
        });
        this.io.observe(target);
        const btn = this.querySelector('[data-gh-sticky-submit]');
        if (btn) {
          btn.addEventListener('click', () => {
            const submit = target.querySelector('[type="submit"], button[name="add"]');
            if (submit) submit.click();
            else target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
      }
      disconnectedCallback() { if (this.io) this.io.disconnect(); }
    });
  }
  /* Tabs: <gh-tabs> with [role=tab] buttons and [role=tabpanel] panels */
  if (!customElements.get('gh-tabs')) {
    customElements.define('gh-tabs', class extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i));
          tab.addEventListener('keydown', (e) => {
            let next = null;
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % this.tabs.length;
            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + this.tabs.length) % this.tabs.length;
            if (e.key === 'Home') next = 0;
            if (e.key === 'End') next = this.tabs.length - 1;
            if (next !== null) { e.preventDefault(); this.select(next); this.tabs[next].focus(); }
          });
        });
      }
      select(index) {
        this.tabs.forEach((t, i) => {
          const on = i === index;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
        });
        this.panels.forEach((p, i) => { p.hidden = i !== index; });
      }
    });
  }

  /* Product recommendations loaded through Shopify's Section Rendering API */
  if (!customElements.get('gh-recommendations')) {
    customElements.define('gh-recommendations', class extends HTMLElement {
      connectedCallback() {
        const url = this.dataset.url;
        if (!url || this.dataset.loaded) return;
        this.dataset.loaded = '1';
        fetch(url)
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('gh-recommendations');
            if (fresh && fresh.innerHTML.trim().length) {
              this.innerHTML = fresh.innerHTML;
            } else {
              const section = this.closest('.shopify-section') || this.closest('.gh-section');
              if (section) section.hidden = true;
            }
          })
          .catch(() => {});
      }
    });
  }

  /* Article table of contents built from the article's H2 headings */
  if (!customElements.get('gh-toc')) {
    customElements.define('gh-toc', class extends HTMLElement {
      connectedCallback() {
        const target = document.querySelector(this.dataset.target || '.gh-article__content');
        const list = this.querySelector('[data-gh-toc-list]');
        if (!target || !list) return;
        const heads = Array.from(target.querySelectorAll('h2'));
        if (heads.length < 2) { this.hidden = true; return; }
        heads.forEach((h, i) => {
          if (!h.id) {
            h.id = (h.textContent || 'section').toLowerCase().trim()
              .replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').slice(0, 60) || ('section-' + (i + 1));
          }
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = '#' + h.id;
          a.textContent = h.textContent;
          li.appendChild(a);
          list.appendChild(li);
        });
      }
    });
  }
})();
